import Vue from "vue";
import {defaultRootClass, RootClass} from "./models/class-structure/RootClass";
import {ClassPanelStatus, classPanelStatusTypesWithResumeData} from "./models/types/ClassPanelStatus";
import {BackEndService} from "../../utils/BackEndService";
import {playClassPool} from "./utils/PlayClassPool";
import router from "../../router";
import {updateClassProgressFromJson} from "./utils/PlayClassFactory";
import {ClassPanelLoadNextClassAction} from "./models/types/ClassTypes";
import {PianoSectionClassController} from "./controllers/piano/PianoSectionClassController";
import {PianoSingleSectionPartClassController} from "./controllers/piano/PianoSingleSectionPartClassController";
import {SingleClassController} from "./controllers/generic/SingleClassController";
import {type AbstractPlayClassController} from "./controllers/generic/AbstractPlayClassController";
import {analyticsController} from "../user/analytics/AnalyticsController";
import {type ClassType} from "./models/types/ClassType";
import {songService} from "../playable/SongService";
import {type PlayClass} from "./models/class-structure/generic/PlayClass";
import {userEventController} from "../user/UserEventController";
import {onboardingChecklistController} from "../onboarding/checklist/OnboardingChecklistController";
import { playerConfig } from "../player/models/PlayerConfig";
import { userService } from "../user/UserService";
import { homeController } from "../catalog/services/HomeController";

interface PlayClassServiceState {
  isClassActive: boolean
  isTransitionPanelDisplayed: boolean
  isClassPanelOpen: boolean
  rootClass: RootClass
  activeClassController: null|AbstractPlayClassController
  activeRootClassType: ClassType
  classPanelStatus: ClassPanelStatus
}

class PlayClassService {
  public readonly state = Vue.observable<PlayClassServiceState>({
    isClassActive: false,
    isTransitionPanelDisplayed: false,
    isClassPanelOpen: false,
    rootClass: defaultRootClass,
    activeClassController: null,
    activeRootClassType: 'PIANO_BOTH',
    classPanelStatus: new ClassPanelStatus()
  })

  get lastActiveClass() {
    if (this.state.classPanelStatus.selectedPanelType in classPanelStatusTypesWithResumeData) {
      if (this.state.classPanelStatus.panelConfigs![this.state.classPanelStatus.selectedPanelType]!.activeClassId) {
        return playClassPool.get(this.state.classPanelStatus.panelConfigs![this.state.classPanelStatus.selectedPanelType]!.activeClassId!)
      }
    }
    return null;
  }

  get isOnLastCLassForActiveClassType() {
    if (this.state.activeClassController) {
      const notFinishedClasses = this.state.rootClass.content[this.state.activeRootClassType]!.containedClasses
        .filter((playClass) => !playClass.progress.isFinished)

      if (notFinishedClasses.length === 1) {
        return (notFinishedClasses[0].id === this.state.activeClassController.playClass.id)
      }
      return false;
    }
    return false;
  }

  /* Class Loading */
  public loadClass(versionId: number) {
    return BackEndService.sendGetRequest(`version/${versionId}/class`)
      .then((response) => {
        playClassPool.reset(); // TODO: Should we reset classPool here ?
        this.state.rootClass = RootClass.createFromJson(versionId, response.content);
        this.state.classPanelStatus = ClassPanelStatus.createFromJson(response.content.piece_version_user_access, this.state.rootClass);
      })
  }

  /* Class Handling */
  loadValidClassController(classId: number) {
    const currentVersion = songService.state.currentVersion;
    if (currentVersion) {
      const ids = userService.user.configurations.pieceCourseIds;
      const courses = homeController.state.pieceCourses;
      if (!ids.includes(currentVersion.id)) {
        // New piece — add to front of both the persisted config and the live array
        userService.updateConfiguration('PIECE_COURSES', [currentVersion.id, ...ids]);
        homeController.state.pieceCourses = [currentVersion, ...courses];
      } else if (ids[0] !== currentVersion.id) {
        // Already known — promote to front
        userService.updateConfiguration('PIECE_COURSES', [currentVersion.id, ...ids.filter(id => id !== currentVersion.id)]);
        const idx = courses.findIndex(c => c.id === currentVersion.id);
        if (idx > 0) {
          homeController.state.pieceCourses = [courses[idx], ...courses.slice(0, idx), ...courses.slice(idx + 1)];
        }
      }
    }

    const playClass = playClassPool.get(classId);
    this.state.activeRootClassType = playClass.type === 'CONSOLIDATION'
      ? 'CONSOLIDATION'
      : this.state.classPanelStatus.selectedPanelType;
    if (playClass.type === 'PIANO_BOTH') {
      if (playClass.playableContent.playableType === 'section') {
        this.state.activeClassController = new PianoSectionClassController(playClass)
      }
      else {
        if (playClass.playableContent.playableTimeline.length === 1) {
          this.state.activeClassController = new PianoSingleSectionPartClassController(playClass)
        }
        else {
          this.state.activeClassController = new SingleClassController(playClass, true)
        }
      }
    }
    else if (playClass.type === 'SING') {
      this.state.activeClassController = new SingleClassController(playClass)
    }
    else {
      this.state.activeClassController = new SingleClassController(playClass, playClass.hasDemo)
    }

    playClassService.state.classPanelStatus.setActiveClassId(classId);
    playClassService.state.isClassActive = true;
  }
  cancelClass() {
    this.state.isClassActive = false;
    return router.push(songService.getUrlWithParams('player')).then(() => {
      playerConfig.resetPlayerConfigState()
    });
  }
  loadNextClassAction(): ClassPanelLoadNextClassAction {
    if (this.lastActiveClass && !this.lastActiveClass.progress.isFinished) {
      return 'LAUNCH_ACTIVE_CLASS'
    }
    else if (this.state.rootClass.content![this.state.classPanelStatus.selectedPanelType]!.hasAtLeaseOneClassAvailableAndNotFinished()) {
      return 'LAUNCH_NEXT_CLASS_AVAILABLE'
    }
    else if (this.state.classPanelStatus.selectedPanelType === 'PIANO_BOTH') {
      if (this.state.rootClass.content.SING && this.state.rootClass.content.SING!.hasAtLeaseOneClassAvailableAndNotFinished() && this.state.rootClass.content.CONSOLIDATION!.progress.isFinished) {
        return 'DISPLAY_SING_PANEL'
      }
    }
    else if (this.state.classPanelStatus.selectedPanelType === 'SING') {
      if (this.state.rootClass.content.PIANO_BOTH!.hasAtLeaseOneClassAvailableAndNotFinished()) {
        return 'DISPLAY_PIANO_BOTH_PANEL'
      }
    }
    else if (this.state.classPanelStatus.selectedPanelType === 'CONSOLIDATION' && this.state.rootClass.content.SING!.hasAtLeaseOneClassAvailableAndNotFinished()) {
      return 'DISPLAY_SING_PANEL'
    }
    return 'NONE'
  }

  /* Step Handling */
  validateStep(classId: number, stepId: number) {
    const activeRootClassType = this.state.activeRootClassType;

    BackEndService.sendPostRequest(`version/${songService.state.currentVersion.id}/class/${classId}/progress`,
      {step_id: stepId, class_resume_status: this.state.classPanelStatus}
    ).then((response) => {
      response.content.forEach((playClass: any) => updateClassProgressFromJson(playClass))

      if (this.state.activeRootClassType !== 'CONSOLIDATION') {
        const currentClass : PlayClass = playClassPool.get(this.state.classPanelStatus.panelConfigs![this.state.activeClassController!.playClass.type]!.panelSelectedClassId!)
        const oldPartProgress = playClassPool.get(this.state.classPanelStatus.panelConfigs![this.state.activeClassController!.playClass.type]!.panelSelectedClassId!).progress.total
        if(oldPartProgress < currentClass.progress.total && currentClass.progress.total === 1){
          if(currentClass.type === "PIANO_BOTH") {
            userEventController.newActivity('COMPLETE_PIECE_PART', currentClass.playableContent.id, currentClass.playableContent.playableType, {})
          }
          else if(currentClass.type === "SING") {
            userEventController.newActivity('COMPLETE_SING_PART', currentClass.playableContent.id, currentClass.playableContent.playableType, {})
          }
        }
      }

      const activeClass = playClassPool.get(classId)
      
      if(!userService.user.configurations.pieceCourseIds.includes(songService.state.currentVersion.id) || userService.user.configurations.notInterestingVersions.includes(songService.state.currentVersion.id)) {
        userService.fetchUserData()
      }

      analyticsController.logBasicAnalyticsEvent('root progress updated', 'Class Events', {
        rootClassType: activeRootClassType,
        rootClassId: this.state.rootClass.content[activeRootClassType]!.id,
        progress: this.state.rootClass.content[activeRootClassType]!.progress.total,
        isRootClassFinished: this.state.rootClass.content[activeRootClassType]!.progress.isFinished,
        updatedClassType: activeClass.type,
        updatedClassContentType: activeClass.playableContent.playableType,
        updatedClassContentIsFinished: activeClass.progress.isFinished,
      })

      if (activeClass.type === 'PIANO_BOTH' && activeClass.playableContent.playableType === 'part' && activeClass.progress.isFinished) {
        onboardingChecklistController.completeTask('first_song')
      }
    })
  }

  uniqueClasses(): PlayClass[] {
    const currentClasses: PlayClass[] = [];
    const partTimeline = playClassService.state.rootClass.playableContent.baseModel.part_timeline;
    const containedClasses = playClassService.state.rootClass.content.PIANO_BOTH!.containedClasses;

    for (const partId of partTimeline) {
      const correspondingClasse = containedClasses.find((classe) => {
        return classe.playableContent.id === partId;
      });
      currentClasses.push(correspondingClasse!);
    }
    return currentClasses;
  }
}

export const playClassService = new PlayClassService();
